import BaseTypedController from "ips/ac/base/controller/BaseTypedController";
import FormElementJoin, { $FormElementJoinSettings } from "ips/ac/control/FormElementJoin";
import Util from "ips/ac/util/Util";
import Button, { Button$PressEvent } from "sap/m/Button";
import Dialog from "sap/m/Dialog";
import { ButtonType } from "sap/m/library";
import Form from "sap/ui/layout/form/Form";
import FormContainer from "sap/ui/layout/form/FormContainer";
import ResponsiveGridLayout from "sap/ui/layout/form/ResponsiveGridLayout";
import Context from "sap/ui/model/Context";
import JSONModel from "sap/ui/model/json/JSONModel";

type EntityType = "ERPCOVERFRAME" | "ERPCASEMENT" | "ERPGLASS" | "ERPFITTING";

interface TreeNode {
  text?: string;
  nodes?: TreeNode[];
  showDetailButton?: boolean;
  entityType?: EntityType;

  [key: string]: unknown;
}

interface ERPCasement extends TreeNode {
  CasementId: string;
  ERPGlassDetails: TreeNode[];
  ERPFittingDetails: TreeNode[];
}

interface ERPCoverFrame extends TreeNode {
  ElementId: string;
  ERPCasementDetails: ERPCasement[];
}

interface DialogField {
  label: string;
  /** one or more properties, joined by FormElementJoin */
  content: string | string[];
}

interface DialogConfig {
  valueHelpPrefix: string;
  fields: DialogField[];
}

const MEASUREMENTS: DialogField = { label: "Measurements", content: ["Width", "Height"] };

const DIALOG_CONFIG: Record<EntityType, DialogConfig> = {
  ERPCOVERFRAME: {
    valueHelpPrefix: "ERPCoverFrame",
    fields: [
      { label: "ElementId", content: ["ElementId"] },
      { label: "System", content: ["Type_Text", "Type"] },
      { label: "SystemAlu", content: ["TypeTextAlu", "TypeValueAlu"] },
      MEASUREMENTS,
      { label: "Color", content: ["Design_Text", "Design_Value"] },
      { label: "ColorAlu", content: ["DesignTextAlu", "DesignValueAlu"] },
      { label: "Exterior", content: ["DesignExterior_Text", "DesignExterior_Value"] },
      { label: "Interior", content: ["DesignInterior_Text", "DesignInterior_Value"] },
    ],
  },
  ERPCASEMENT: {
    valueHelpPrefix: "ERPCasement",
    fields: [
      { label: "System", content: ["Type_Text", "Type"] },
      { label: "SystemAlu", content: ["TypeTextAlu", "TypeValueAlu"] },
      MEASUREMENTS,
      { label: "Color", content: ["Design_Text", "Design_Value"] },
      { label: "ColorAlu", content: ["DesignTextAlu", "DesignValueAlu"] },
      { label: "Exterior", content: ["DesignExterior_Text", "DesignExterior_Value"] },
      { label: "Interior", content: ["DesignInterior_Text", "DesignInterior_Value"] },
      { label: "GasketStop", content: ["GasketStopLongText", "GasketStopShortText"] },
      { label: "HandlePosition", content: ["HandlePositionText", "HandlePosition_Value"] },
      { label: "FittingParameter", content: ["FittingParameter"] },
      { label: "GroupNumber", content: ["GroupNumber"] },
    ],
  },
  ERPGLASS: {
    valueHelpPrefix: "ERPGlass",
    fields: [
      { label: "ShortText", content: ["ShortText"] },
      { label: "LongText", content: ["LongText"] },
      { label: "SalesText", content: ["SalesText"] },
      { label: "Description", content: ["Description"] },
      MEASUREMENTS,
      { label: "Depth", content: ["Depth"] },
      { label: "SpecialGlass01", content: ["SpecialGlass01"] },
      { label: "SpecialGlass02", content: ["SpecialGlass02"] },
    ],
  },
  ERPFITTING: {
    valueHelpPrefix: "ERPFitting",
    fields: [
      { label: "OrderNumber", content: "OrderNumber" },
      { label: "Type_Text", content: "TypeText" },
      { label: "Type_Value", content: "Type_Value" },
      { label: "ArticleType_Text", content: "ArticleTypeText" },
      { label: "ArticleType_Value", content: "ArticleType_Value" },
      { label: "Manufacturer_Text", content: "ManufacturerText" },
      { label: "Manufacturer_Value", content: "Manufacturer_Value" },
      { label: "SortingIndicator", content: "SortingIndicator" },
      { label: "Length1", content: "Length1" },
      { label: "Length2", content: "Length2" },
    ],
  },
};

/**
 * Shows the ERP cover frames of the bound product as tree. Lives in its own view so the
 * data is only requested once the ObjectPageLazyLoader of the parent page instantiates it.
 * @namespace ips.ac.data.product.controller
 */
export default class ProductConstruction extends BaseTypedController {
  private coverFrameModel: JSONModel;
  private productPath: string | null = null;
  private detailDialog: Dialog;

  public onInit(): void {
    super.onInit();
    this.coverFrameModel = new JSONModel([], true);
    this.setModel(this.coverFrameModel, "ERPCoverFrames");
    this.getView().attachModelContextChange(this.onContextChange, this);
    // the context may already be propagated when the lazy loader instantiates the view
    this.onContextChange();
  }

  public onExit(): void {
    this.detailDialog?.destroy();
  }

  private onContextChange(): void {
    const path = this.getView().getBindingContext()?.getPath();
    if (!path || path === this.productPath) {
      return;
    }
    this.productPath = path;
    this.coverFrameModel.setProperty("/", []);
    void this.loadCoverFrames(path);
  }

  public showAlternatives(event: Button$PressEvent): void {
    const context = event.getSource().getBindingContext("ERPCoverFrames");
    const dialog = this.getDetailDialog();
    this.setDialogData(context, dialog);
    dialog.open();
  }

  private async loadCoverFrames(productPath: string): Promise<void> {
    const binding = Util.getODataModel().bindList(productPath + "/ERPCoverFrameDetails", undefined, [], [], {
      $expand: "ERPCasementDetails($expand=ERPGlassDetails,ERPFittingDetails)",
      $orderby: "ElementId asc",
    });
    try {
      const contexts = await binding.requestContexts(0, Infinity);
      // ignore late responses of a previously bound product
      if (productPath !== this.productPath) {
        return;
      }
      const coverFrames = contexts.map((context) => context.getObject() as ERPCoverFrame);
      coverFrames.forEach((coverFrame) => this.toCoverFrameNode(coverFrame));
      this.coverFrameModel.setProperty("/", coverFrames);
    } finally {
      binding.destroy();
    }
  }

  private toCoverFrameNode(coverFrame: ERPCoverFrame): void {
    coverFrame.ERPCasementDetails.sort((a, b) => a.CasementId.localeCompare(b.CasementId));
    coverFrame.text = Util.getText("@ERPCasementERPCoverFrameDetail") + " " + coverFrame.ElementId;
    coverFrame.nodes = coverFrame.ERPCasementDetails;
    coverFrame.entityType = "ERPCOVERFRAME";
    coverFrame.showDetailButton = true;
    coverFrame.ERPCasementDetails.forEach((casement) => this.toCasementNode(casement));
  }

  private toCasementNode(casement: ERPCasement): void {
    casement.nodes = [];
    if (casement.ERPGlassDetails.length > 0) {
      casement.ERPGlassDetails.forEach((glass) => {
        glass.text = glass.ShortText as string;
        glass.showDetailButton = true;
        glass.entityType = "ERPGLASS";
      });
      casement.nodes.push(this.createGroupNode("@ERPGlasses", casement.ERPGlassDetails));
    }
    if (casement.ERPFittingDetails.length > 0) {
      casement.ERPFittingDetails.forEach((fitting) => {
        fitting.text = fitting.OrderNumberText as string;
        fitting.showDetailButton = true;
        fitting.entityType = "ERPFITTING";
      });
      casement.nodes.push(this.createGroupNode("@ERPFitting", casement.ERPFittingDetails));
    }
    casement.text = Util.getText("@ERPCasement") + " " + casement.CasementId;
    casement.showDetailButton = true;
    casement.entityType = "ERPCASEMENT";
  }

  private createGroupNode(textKey: string, children: TreeNode[]): TreeNode {
    return {
      text: Util.getText(textKey) + " (" + children.length + ")",
      nodes: children,
      showDetailButton: false,
    };
  }

  private getDetailDialog(): Dialog {
    if (!this.detailDialog) {
      this.detailDialog = new Dialog({
        contentWidth: "auto",
        contentHeight: "auto",
        horizontalScrolling: false,
        verticalScrolling: false,
        endButton: new Button({
          type: ButtonType.Emphasized,
          text: Util.getText("Close"),
          press: () => this.detailDialog.close(),
        }),
      });
      this.getView().addDependent(this.detailDialog);
    }
    return this.detailDialog;
  }

  private setDialogData(context: Context, dialog: Dialog): void {
    const config = DIALOG_CONFIG[context.getProperty("entityType") as EntityType];
    const formElements = config ? this.createFormElements(context, config) : [];
    const form = new Form({
      layout: new ResponsiveGridLayout({
        labelSpanXL: 4,
        emptySpanXL: 0,
        columnsXL: 0,
      }),
      formContainers: new FormContainer({ formElements }),
    });
    dialog.setTitle(context.getProperty("text"));
    dialog.destroyContent();
    dialog.addContent(form);
  }

  private createFormElements(context: Context, config: DialogConfig): FormElementJoin[] {
    return config.fields.map((field) => {
      const properties = Array.isArray(field.content) ? field.content : [field.content];
      const settings: $FormElementJoinSettings = {
        label: this.getTranslation(field.label, config.valueHelpPrefix),
        data: properties.map((property) => context.getProperty(property)),
      };
      if (field.label === MEASUREMENTS.label) {
        settings.separator = " x ";
        settings.additionalText = " mm";
      }
      return new FormElementJoin(settings);
    });
  }

  private getTranslation(label: string, valueHelpPrefix: string): string {
    const lastSegment = label.split("/").pop();
    return Util.getText("@" + valueHelpPrefix + (label.includes("/") ? lastSegment : label));
  }
}
